/**
 * RNR's Toggle Group, wearing the design system's segmented frame.
 *
 * The registry component is shadcn's outlined button row; the fleet's segmented control is one
 * frame with no gaps and no radius on the inner edges, which is the shape that says "exactly one
 * of these, and the set is small and fixed".
 *
 * `type="single"` and never `multiple`, which is the whole reason it is this primitive rather than
 * Tabs: it is a value, not a view. One item is always on — a press that would deselect the
 * current item is ignored by `ToggleGroupItem`, because a segmented control with nothing chosen is
 * a control that has lost the setting it exists to hold.
 */
import { TextClassContext } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import * as ToggleGroupPrimitive from '@rn-primitives/toggle-group';
import { Platform } from 'react-native';

function ToggleGroup({ className, ...props }: React.ComponentProps<typeof ToggleGroupPrimitive.Root>) {
  return (
    <ToggleGroupPrimitive.Root
      className={cn('border-seg-line flex-row overflow-hidden rounded-md border', className)}
      {...props}
    />
  );
}

/**
 * One option. `ring` is the selected mark and comes from the caller, because it is the one thing
 * the two looks disagree about — Bouquet rings in `gold-edge`, Tuxedo in `line-strong`.
 *
 * Every item carries the border WIDTH and only the selected one colours it, or the row's items
 * would shift two pixels as the selection moved.
 */
function ToggleGroupItem({
  className,
  ring,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item> & {
  /** The 2px mark on the selected item, as a border-colour class. */
  ring?: string;
}) {
  const { value, onValueChange, type } = ToggleGroupPrimitive.useRootContext();
  const on = ToggleGroupPrimitive.utils.getIsSelected(value, props.value);

  return (
    <TextClassContext.Provider value={cn('text-small font-semibold', on ? 'text-seg-on-ink' : 'text-seg-off-ink')}>
      <ToggleGroupPrimitive.Item
        className={cn(
          'flex-1 items-center border-2 border-transparent px-3 py-1.5',
          on && cn('bg-seg-on', ring),
          Platform.select({ web: 'cursor-default whitespace-nowrap transition-colors disabled:pointer-events-none' }),
          props.disabled && 'opacity-50',
          className,
        )}
        {...props}
        onPress={() => {
          // A press on the item already on would clear the value. This control always holds one,
          // so the press is simply nothing — the same gesture a radio makes.
          if (on) return;
          if (type === 'single') onValueChange(props.value);
          else onValueChange(ToggleGroupPrimitive.utils.getNewMultipleValue(value, props.value));
        }}
      />
    </TextClassContext.Provider>
  );
}

export { ToggleGroup, ToggleGroupItem };
